import React, { useMemo, useEffect, useState } from "react";
import merge from "lodash.merge";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import type { Padding } from "@twilio-paste/style-props";
import r2wc from "react-to-webcomponent"

import { store } from "./store/store";
import { api } from "./api";
import { WebchatWidget } from "./components/WebchatWidget";
import { CachedThemeProvider } from "./contexts/CachedThemeProvider"
import { preferencesDataHandler } from "./preferencesDataHandler";
import { smartHubDataHandler } from "./smartHubDataHandler";
import { initConfig } from "./store/actions/initActions";
import { changeExpandedStatus, updatePreEngagementData } from "./store/actions/genericActions"
import { ConfigState } from "./definitions";
import { initLogger } from "./logger";

const defaultConfig: ConfigState = {
    serverUrl: "http://localhost:3001",
    theme: {
        isLight: true
    },
    fileAttachment: {
        enabled: true,
        maxFileSize: 16777216, // 16 MB
        acceptedExtensions: ["jpg", "jpeg", "png", "amr", "mp3", "mp4", "pdf", "txt"]
    },
    transcript: {
        downloadEnabled: false,
        emailEnabled: false,
        emailSubject: (agentNames) => {
            let subject = "Transcript of your chat";
            if (agentNames.length > 0) {
                subject = subject.concat(` with ${agentNames[0]}`);
                agentNames.slice(1).forEach((name) => (subject = subject.concat(` and ${name}`)));
            }
            return subject;
        },
        emailContent: (customerName, transcript) => {
            return `<div><h1 style="text-align:center;">Chat Transcript</h1><p>Hello ${customerName},<br><br>Please see below your transcript, with any associated files attached, as requested.<br><br>${transcript}</p></div>`;
        }
    }
};

async function initConfiguration(siteConfig: ConfigState) {
    const configuration = merge({ theme: { overrides: {} } }, defaultConfig, siteConfig);
    api.setEndpoint(configuration.serverUrl);
    api.setClientId(siteConfig.clientId);

    const [remoteConfig, smartHubProfile] = await Promise.allSettled([
        preferencesDataHandler.getRemotePreferences(),
        smartHubDataHandler.getRemoteSmarthubData()
    ])

    if (remoteConfig.status === "fulfilled") {
        configuration.theme.overrides = merge(remoteConfig.value, configuration.theme?.overrides);
    }

    if (smartHubProfile.status === "fulfilled" && smartHubProfile.value) {
        store.dispatch(updatePreEngagementData({
            name: `${smartHubProfile.value.firstName} ${smartHubProfile.value.lastName}`,
            email: smartHubProfile.value.email
        }))
    }

    store.dispatch(initConfig(configuration));
    return configuration
}

type ExternalProps = ConfigState & {
    container: ShadowRoot
}

const elementProps = {
    MESSAGE_INPUT: {
        boxShadow: "none!important" as "none"
    },
    MESSAGE_INPUT_BOX: {
        display: "inline-block",
        boxShadow: "none" as const
    },
    ALERT: {
        paddingTop: "space30" as Padding,
        paddingBottom: "space30" as Padding
    },
    BUTTON: {
        "&[aria-disabled='true'][color='colorTextLink']": {
            color: "colorTextLinkWeak"
        }
    }
}

const Component = ({ mode, clientId, serverUrl, container }: ExternalProps) => {
    initLogger()
    const [isReady, setReady] = useState(false)

    const cssCache = useMemo(() => {
        if (!container) return null
        return {
            key: 'css-global',
            prepend: true,
            container
        }
    }, [container])

    useEffect(() => {

        async function updateConfig() {
            try {
                await initConfiguration({
                    serverUrl,
                    clientId,
                });
                setReady(true)
            }
            catch (e) {
                console.error("error updating configutation", e)
            }
        }

        updateConfig()

    }, [clientId, serverUrl])

    useEffect(() => {
        if (!isReady) return
        window.dispatchEvent(new Event("sh:webchat:ready"))
    }, [isReady])

    useEffect(() => {

        const expanded = mode === "fullscreen"
        store.dispatch(changeExpandedStatus({ expanded }))
        store.dispatch(initConfig({ mode }));

    }, [mode])

    if (!cssCache || !isReady) return null

    return (
        <React.StrictMode>
            <CachedThemeProvider
                cacheProps={cssCache}
                elements={elementProps}
                theme={store.getState().config.theme?.overrides}
                style={{ minHeight: "100%", minWidth: "100%" }}>
                <Provider store={store}>
                    <WebchatWidget />
                </Provider>
            </CachedThemeProvider>
        </React.StrictMode>
    )
}

const ChatWebComponent = r2wc<ExternalProps>(Component, React, ReactDOM, {
    shadow: "open",
    props: {
        clientId: "string",
        mode: "string",
        serverUrl: "string"
    }
})

customElements.define("nisc-chat", ChatWebComponent);